import React, { FC } from 'react';

import { Grid, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Box } from '@mui/system';

import {
  ComparePaymentPlanTypes,
  PaymentPlans,
  PharmacyBillingPageStatuses,
} from '@pharmaplan/common';

import useComparePaymentPlan from '../../hooks/Payment/useComparePaymentPlan';
import ComparePlansTable from './ComparePaymentTable/ComparePlansTable';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { resetDialog } from '../../reducers/dialogReducer';
import CustomButton from '../common/CustomButton';
import strings from '../../localization';

import styles from './styles';

interface IComparePaymentPlanUpgrade {
  screenType: ComparePaymentPlanTypes.upgrade;
  setPaymentPlan?: React.Dispatch<React.SetStateAction<PaymentPlans>>;
  setPageStatus: React.Dispatch<
    React.SetStateAction<PharmacyBillingPageStatuses>
  >;
}

interface IComparePaymentPlanCompare {
  screenType: ComparePaymentPlanTypes.compare;
  setPaymentPlan: React.Dispatch<React.SetStateAction<PaymentPlans>>;
  setPageStatus?: React.Dispatch<
    React.SetStateAction<PharmacyBillingPageStatuses>
  >;
}

interface IComparePaymentPlanView {
  screenType: ComparePaymentPlanTypes.view;
  setPaymentPlan?: React.Dispatch<React.SetStateAction<PaymentPlans>>;
  setPageStatus?: React.Dispatch<
    React.SetStateAction<PharmacyBillingPageStatuses>
  >;
}

type IComparePaymentPlan =
  | IComparePaymentPlanCompare
  | IComparePaymentPlanUpgrade
  | IComparePaymentPlanView;

const ComparePaymentPlan: FC<IComparePaymentPlan> = ({
  screenType,
  setPaymentPlan,
  setPageStatus,
}) => {
  const dispatch = useAppDispatch();
  const isCompare = screenType === ComparePaymentPlanTypes.compare
    || screenType === ComparePaymentPlanTypes.view;
  const { config } = useComparePaymentPlan(setPaymentPlan, setPageStatus);
  const {
    title,
    firstButtonLabel = '',
    firstButtonAction,
    secondButtonAction,
    secondButtonLabel = '',
  } = config[screenType] ?? {};

  const onClickClose = () => {
    dispatch(resetDialog());
  };

  return (
    <Box sx={[styles.container, !isCompare && styles.removeTopPadding]}>
      <Box sx={styles.headerContainer}>
        <Typography sx={styles.title}>{title}</Typography>
        {isCompare && (
          <IconButton size="small" sx={styles.closeIcon} onClick={onClickClose}>
            <Close />
          </IconButton>
        )}
      </Box>
      <Typography sx={styles.description}>
        {strings.fullDetailedFeatures}
      </Typography>

      <ComparePlansTable />

      {firstButtonLabel && secondButtonLabel && (
        <Grid container>
          <Grid item xs={4} sx={styles.rowContainer} />
          <Grid item xs={4} sx={styles.rowContainer}>
            <CustomButton
              customButtonStyle={styles.button}
              label={firstButtonLabel}
              onClick={firstButtonAction}
            />
          </Grid>
          <Grid item xs={4} sx={styles.rowContainer}>
            <CustomButton
              customButtonStyle={styles.button}
              label={secondButtonLabel}
              onClick={secondButtonAction}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ComparePaymentPlan;
